/**
 * Shared controller for the left-edge floors elevation map on tools that work
 * against the project's PERSISTED floor groups (selection tools, manual floor
 * drawing). Builds the bars from the persona's floor group data, keeps the
 * active floor highlighted, rebuilds on floor changes, and turns a bar click
 * into the standard `select_group` flow — the tool's own viewport
 * interactions stay untouched.
 *
 * (The auto-floor flow has its own richer, proposal-driven panel in
 * FloorToolBase.)
 */

import * as THREE from 'three';
import { FALLBACK_INTERIOR_HEIGHT_M, SLAB_SURFACE_MARGIN_M } from '@pointorama/potree-shared';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { FloorBarsOverlay, FloorBarsOptions } from '../../../utils/FloorBarsOverlay';
import { DetectedFloorLevel } from '../../../utils/floorDetection';
import { FloorIsolationStrategy, IsolationSpec } from '../../../utils/FloorIsolation';
import { FloorplanPersona, FloorGroupData } from '../../personas/implementations/FloorplanPersona';

export class FloorBarsPanelController {
  private readonly viewer: Viewer;
  private readonly persona: IPersona;
  private readonly overlayOptions: FloorBarsOptions | undefined;
  private overlay: FloorBarsOverlay | null = null;
  private groupIds: string[] = [];
  private groups: FloorGroupData[] = [];
  private enabled = false;

  /** Isolation spec saved when a hover temporarily replaced it. */
  private hoverSavedSpec: { spec: IsolationSpec | null; groupId: string | null } | null = null;

  constructor(viewer: Viewer, persona: IPersona, overlayOptions?: FloorBarsOptions) {
    this.viewer = viewer;
    this.persona = persona;
    this.overlayOptions = overlayOptions;
  }

  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.persona.addEventListener('floor_data_changed', this.handleFloorDataChanged);
    this.persona.addEventListener('floor_selection_changed', this.handleFloorSelectionChanged);
    this.rebuild();
  }

  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    this.persona.removeEventListener('floor_data_changed', this.handleFloorDataChanged);
    this.persona.removeEventListener('floor_selection_changed', this.handleFloorSelectionChanged);
    this.restoreHoverIsolation();
    this.overlay?.clear();
  }

  private handleFloorDataChanged = (): void => {
    this.rebuild();
  };

  private handleFloorSelectionChanged = (): void => {
    // A selection change supersedes any saved pre-hover isolation — restoring
    // it on hover-out would resurrect a stale clip.
    this.hoverSavedSpec = null;
    this.syncActive();
  };

  private floorplanPersona(): Partial<FloorplanPersona> {
    return this.persona as Partial<FloorplanPersona>;
  }

  private rebuild(): void {
    const persona = this.floorplanPersona();
    if (typeof persona.getFloorGroupsData !== 'function') return;
    const groups = persona.getFloorGroupsData();

    if (!this.overlay) {
      this.overlay = new FloorBarsOverlay(
        this.viewer.renderArea,
        {
          onHover: (index) => this.handleBarHover(index),
          onClick: (index) => this.handleBarClick(index),
        },
        this.overlayOptions
      );
    }

    this.groups = groups;
    this.groupIds = groups.map((group) => group.groupIdentifier);
    const levels: DetectedFloorLevel[] = groups.map((group) => ({
      name: group.name,
      floor: group.floor,
      ceiling: group.ceiling ?? null,
      // Persisted groups may still carry legacy next-floor ceilings.
      ceilingKind: 'next-floor' as const,
      // Per-floor persisted footprint gives the bar a real relative width.
      footprintCorners: group.footprintCorners?.map((c) => new THREE.Vector2(c.x, c.y)),
    }));
    this.overlay.setLevels(levels);
    this.syncActive();
  }

  /** Bar hover highlights AND temporarily isolates the hovered floor. */
  private handleBarHover(index: number | null): void {
    this.overlay?.setHovered(index ?? -1);
    const manager = this.viewer.floorIsolationManager;

    if (index !== null) {
      const strategy = this.hoverIsolationStrategy(index);
      if (!strategy) return;
      // Save whatever clip was active once per hover session, restore on leave.
      if (!this.hoverSavedSpec) {
        this.hoverSavedSpec = { spec: manager.getActiveSpec(), groupId: manager.getActiveFloorGroupId() };
      }
      manager.setCustomSpec({ base: strategy }, this.groupIds[index] ?? null);
      return;
    }

    this.restoreHoverIsolation();
  }

  private restoreHoverIsolation(): void {
    if (!this.hoverSavedSpec) return;
    const saved = this.hoverSavedSpec;
    this.hoverSavedSpec = null;
    const manager = this.viewer.floorIsolationManager;
    if (saved.spec) manager.setCustomSpec(saved.spec, saved.groupId);
    else manager.clearCustomStrategy();
  }

  /** Hovered floor's clip: its own footprint box, else an XY-infinite slab.
   *  Padded by the room tooling's slab-surface margin — the planes are fitted
   *  THROUGH the slab scatter, so an exact clip halves the surface points. */
  private hoverIsolationStrategy(index: number): FloorIsolationStrategy | null {
    const group = this.groups[index];
    if (!group) return null;
    const floorZ = group.floor.height;
    const ceilingZ = group.ceiling?.height ?? floorZ + FALLBACK_INTERIOR_HEIGHT_M;
    const corners = group.footprintCorners;
    if (corners && corners.length >= 4) {
      return {
        kind: 'box',
        xyCorners: [new THREE.Vector2(corners[0].x, corners[0].y), new THREE.Vector2(corners[1].x, corners[1].y), new THREE.Vector2(corners[2].x, corners[2].y), new THREE.Vector2(corners[3].x, corners[3].y)],
        minZ: floorZ - SLAB_SURFACE_MARGIN_M,
        maxZ: ceilingZ + SLAB_SURFACE_MARGIN_M,
      };
    }
    return { kind: 'roomBounds', floorGroupId: group.groupIdentifier, floorHeight: floorZ, ceilingHeight: ceilingZ, margin: SLAB_SURFACE_MARGIN_M };
  }

  private syncActive(): void {
    const persona = this.floorplanPersona();
    const selected = typeof persona.getSelectedFloorGroupIdentifier === 'function' ? persona.getSelectedFloorGroupIdentifier() : null;
    this.overlay?.setPreviewed(selected ? this.groupIds.indexOf(selected) : -1);
  }

  /**
   * Bar click = switch the active floor through the same select_group flow
   * the hierarchy uses; the persona's floor_selection_changed then updates
   * isolation and every listening tool.
   */
  private handleBarClick(index: number): void {
    // The click's selection supersedes the pre-hover clip — don't restore it
    // when the pointer later leaves the bar.
    this.hoverSavedSpec = null;
    const groupIdentifier = this.groupIds[index];
    if (!groupIdentifier) return;
    const persona = this.floorplanPersona();
    const selected = typeof persona.getSelectedFloorGroupIdentifier === 'function' ? persona.getSelectedFloorGroupIdentifier() : null;
    if (groupIdentifier === selected) return;
    this.viewer.scene.dispatchEvent({
      type: 'select_group',
      value: { groupIdentifier },
    });
  }
}
